import { Group, Select, TextInput } from '@mantine/core';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { StrategicEpic } from '../../../api/tasks-client/task-client.type';
import { useTranslation } from 'react-i18next';
import { useStrategyStore } from '../../../store/strategy-store/strategy-store';
import { setTableGroupBy } from '../../../store/strategy-store/strategy-store.actions';
import { TableGroupBy } from '../../../store/strategy-store/strategy-store.type';
import { baseWhite, inkLight } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Icon } from '../../../ui-library/icon/icon';

interface EpicsMainSectionControlsProps {
  epics: StrategicEpic[];
  onSearch: (term: string) => void;
  onExport: () => void;
  searchTerm: string;
}

export const EpicsMainSectionControls = ({
  epics: _epics,
  onSearch,
  onExport,
  searchTerm,
}: EpicsMainSectionControlsProps) => {
  const i18n = useTranslation();
  const tableGroupBy = useStrategyStore((state) => state.tableGroupBy);

  // Local state gives instant visual feedback; the store update is debounced separately
  const [localValue, setLocalValue] = useState(searchTerm);

  // onSearch is stable (wrapped in useCallback upstream), so this debounce is created once
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        onSearch(value);
      }, 200),
    [onSearch],
  );

  // Keep localValue in sync if the search term is cleared externally (e.g. filter reset).
  // Also cancel any pending debounced call so it doesn't re-apply the stale value after the reset.
  const prevSearchTerm = useRef(searchTerm);
  useEffect(() => {
    if (searchTerm !== prevSearchTerm.current) {
      prevSearchTerm.current = searchTerm;
      if (searchTerm === '') {
        setLocalValue('');
        debouncedSearch.cancel();
      }
    }
  }, [searchTerm, debouncedSearch]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearchChange = (value: string) => {
    setLocalValue(value);
    debouncedSearch(value);
  };

  const groupByOptions = [{ value: TableGroupBy.Team, label: i18n.t('strategy.epics.contributing_teams') }];

  return (
    <Group justify="space-between" miw={760}>
      <Group gap={16}>
        <Button
          variant="secondary"
          radius="xl"
          size="sm"
          leftSection={<Icon name="format_list_bulleted" color={baseWhite} size={16} style={{ cursor: 'pointer' }} />}
        >
          Table View
        </Button>
        <Select
          placeholder="Group by..."
          data={groupByOptions}
          value={tableGroupBy || ''}
          onChange={(value: string | null) => setTableGroupBy((value as TableGroupBy) || null)}
          clearable={true}
          allowDeselect={true}
          radius="xl"
          size="sm"
          w={125}
        />
      </Group>
      <Group>
        <TextInput
          placeholder={`Search by ${i18n.t('common.epic').toLowerCase()} name or ID`}
          radius="xl"
          size="sm"
          value={localValue}
          leftSection={<Icon name="search" variant="filled" size={16} color={inkLight} />}
          style={{ width: 300 }}
          onChange={(event) => handleSearchChange(event.currentTarget.value)}
        />
        <Button
          variant="secondary"
          radius="xl"
          size="sm"
          leftSection={
            <Icon name="download" color={baseWhite} variant="filled" size={16} style={{ cursor: 'pointer' }} />
          }
          onClick={onExport}
        >
          Export
        </Button>
      </Group>
    </Group>
  );
};
